<script>
import { isNil, isObject } from "lodash";
import moment from "moment";

export default {
  props: {
    calculos: {
      type: Array,
      default: () => []
    },
    acumulados: {
      type: Array,
      default: () => []
    },
    competencias: {
      type: Array,
      default: () => []
    },
    endMonth: {
      type: String,
      default: ""
    }
  },
  computed: {
    headers() {
      const months = this.competencias.map(value => ({
        align: "end",
        divider: value === this.endMonth,
        text: moment(value, "YYYY-MM").format("MM/YYYY"),
        value,
        width: "150px"
      }));

      let firstLabel = "";
      if (this.calculos && this.calculos.length > 0) {
        firstLabel = this.calculos[0].label || "";
      }

      return [
        {
          text: firstLabel,
          value: "rubrica",
          width: months.length > 0 ? "300px" : ""
        },
        ...months
      ];
    },
    headerClass() {
      return `vuetify-table-header${this.getClient().isClient ? "-cli" : ""}`;
    },
    tableItems() {
      let items = [];
      const allMonths = this.competencias;
      const toCurrencyValue = this.$options.filters.toCurrencyValue;

      const processRows = rows => {
        const FIRST_LABEL = "RxO";
        const SECOND_LABEL = "Retorno Financeiro";

        return rows
          .map(({ competencias, rubrica, ...rest }) => {
            let parsed = {
              ...rest,
              rubrica,
              type: "normal"
            };

            if (isObject(competencias) && !isNil(competencias)) {
              allMonths.forEach(key => {
                let value = " -- ";

                if (key in competencias) {
                  if (rubrica === FIRST_LABEL) {
                    value =
                      typeof competencias[key] === "string"
                        ? competencias[key].toUpperCase()
                        : competencias[key];
                  } else {
                    value =
                      typeof competencias[key] === "number"
                        ? "R$ " + toCurrencyValue(competencias[key])
                        : competencias[key];
                  }
                }

                parsed[key] = value;
              });
            }

            return parsed;
          })
          .sort(({ rubrica: a = "" }, { rubrica: b = "" }) => {
            if (a === FIRST_LABEL) return -1;
            if (b === FIRST_LABEL) return 1;

            if (a === SECOND_LABEL) return -1;
            if (b === SECOND_LABEL) return 1;

            return a.localeCompare(b);
          });
      };

      if (!this.calculos || this.calculos.length === 0) {
        return [];
      }

      // Projetado
      if (this.calculos[0]) {
        items.push(...processRows(this.calculos[0].rows));
      }

      // Realizado
      if (this.calculos[1]) {
        items.push({
          type: "header",
          label: this.calculos[1].label,
          _key: "header-realizado"
        });
        items.push(...processRows(this.calculos[1].rows));
      }

      // Acumulados
      if (this.acumulados && this.acumulados.length > 0) {
        items.push({
          type: "header-acumulado",
          label: "KPI de referência",
          _key: "header-acumulados"
        });

        const mappedAcum = this.acumulados.map((item, idx) => ({
          ...item,
          type: "acumulado",
          _key: `acum-${idx}`
        }));
        items.push(...mappedAcum);
      }

      items.forEach((item, index) => {
        item._uid = item._key || `row-${index}`;
      });

      return items;
    }
  },
  methods: {
    getItemClass(row) {
      if (row.type !== "normal") return "";

      return {
        highlight:
          row.rubrica === "Retorno Financeiro" && row.rubrica_id === null,
        "like-header": row.rubrica === "RxO" && row.rubrica_id === null
      };
    }
  }
};
</script>

<template>
  <v-data-table
    data-app
    disable-filtering
    disable-pagination
    disable-sort
    :headers="headers"
    height="auto"
    hide-default-footer
    hide-default-header
    :items="tableItems"
    item-key="_uid"
    no-data-text="Não existem dados para mostrar."
  >
    <template v-slot:header>
      <thead class="v-data-table-header">
        <tr>
          <th
            v-for="(col, i) in headers"
            :key="i"
            :class="[
              headerClass,
              { divider: col.divider },
              `text-${col.align || 'start'}`,
              'text-truncate'
            ]"
            :style="{ minWidth: col.width || '150px' }"
            role="columnheader"
            scope="col"
          >
            <span>
              {{ col.text }}
            </span>
          </th>
        </tr>
      </thead>
    </template>

    <template v-slot:item="{ item, headers }">
      <tr v-if="item.type === 'header'" class="like-header">
        <th class="text-start">{{ item.label }}</th>
        <th
          v-for="(col, i) in headers.slice(1)"
          :key="i"
          :class="[{ divider: col.divider }, `text-${col.align || 'start'}`]"
        >
          {{ col.text }}
        </th>
      </tr>

      <tr v-else-if="item.type === 'header-acumulado'" class="like-header">
        <th class="text-start">{{ item.label }}</th>
        <th :colspan="headers.length - 1"></th>
      </tr>

      <tr v-else-if="item.type === 'acumulado'" class="acumulado-row">
        <td class="text-start">{{ item.label }}</td>
        <td class="text-end">
          {{ item.value }}
        </td>
        <td :colspan="headers.length - 2" />
      </tr>

      <tr v-else :class="getItemClass(item)">
        <td class="text-start">{{ item.rubrica }}</td>
        <td
          v-for="(col, i) in headers.slice(1)"
          :key="i"
          :class="[{ divider: col.divider }, `text-${col.align || 'start'}`]"
        >
          {{ item[col.value] }}
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<style lang="scss" scoped>
.v-data-table {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  min-height: 0;

  &::v-deep > .v-data-table__wrapper {
    overflow: auto;
    width: 100%;
    flex-grow: 1;

    > table {
      > thead > tr > th {
        position: sticky;
        top: 0;
        z-index: 3;
        background-color: var(--v-table-header-base);
        border-bottom: 0px !important;

        &:first-child {
          left: 0;
          z-index: 4;
        }
      }

      > tbody > tr {
        background-color: #fff;

        > td:first-child,
        > th:first-child {
          position: sticky;
          left: 0;
          z-index: 1;
          background-color: inherit;
        }

        &.highlight {
          background-color: #eeeeee;

          td {
            font-weight: bold;
          }
        }

        &.like-header {
          background-color: var(--v-table-header-base);

          > th {
            color: #3e3e3f;
            font-family: Poppins;
            font-style: normal;
            font-weight: 400;
            font-size: 13px;
            line-height: 19px;
            letter-spacing: 0.01em;
          }

          &:hover {
            background-color: var(--v-table-header-base) !important;
          }
        }

        &:hover {
          background-color: #f5f5f5;
        }
      }
    }

    th,
    td {
      &.divider {
        border-right: 2px solid #f43336 !important;
      }
    }
  }
}

.v-data-table::v-deep .v-data-table__empty-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
</style>
